import { SkeletonTable } from '../../components/LoadingSkeleton/LoadingSkeleton';
import { splitPhone } from '../../utils/phone';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import TrashIcon from '../../components/icons/TrashIcon';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchOffices,
  fetchDealerOffices,
  addOfficeAsync,
  editOfficeAsync,
  deleteOfficeAsync,
  clearMessages,
  selectOffices,
  selectOfficesLoading,
  selectOfficesError,
  selectSuccessMessage,
  selectRefreshOfficesData,
  setRefreshOfficesData
} from '../../features/offices/officesSlice';
import './offices.css';
import { Eye, EyeOff } from 'lucide-react';

const digitsOnly = (value, maxLength) => value.replace(/\D/g, '').slice(0, maxLength);
const cityOnly = (value) => value.replace(/[^a-zA-Z\s.'-]/g, '');
const preventNonNumericInput = (event) => {
  if (event.ctrlKey || event.metaKey || ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
  if (!/^\d$/.test(event.key)) event.preventDefault();
};

const Offices = () => {
  const dispatch = useDispatch();
  const { offices: allOffices, loading } = useSelector((state) => state.offices);
  const successMessage = useSelector(selectSuccessMessage);
  const errorMessage = useSelector(selectOfficesError);
  const refreshData = useSelector(selectRefreshOfficesData);

  // Get user role from Redux/auth state
  const { user } = useSelector((state) => state.auth);

  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [goToPage, setGoToPage] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);
  const [showAlert, setShowAlert] = useState(false);
  const [alertType, setAlertType] = useState('');
  const [alertMessage, setAlertMessage] = useState('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedOffice, setSelectedOffice] = useState(null);

  // Modal states for Create/Edit
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingOffice, setEditingOffice] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  // US States list
  const usStates = [
    "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
    "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
    "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
    "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
    "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY", "INDIA"
  ];

  // Form Data for Office
  const [officeFormData, setOfficeFormData] = useState({
    officeName: '',
    officeMobile1: '',
    officeMobile2: '',
    phoneCode1: '+1',
    phoneCode2: '+1',
    officeemail: '',
    officepassword: '',
    officeaddress: '',
    city: '',
    state: '',
    zipcode: '',
    isbar: '0',
    officelicense: '',
    dealerId: ''
  });

  // Auto-generate office name from email
  const onEmailInput = (email) => {
    setOfficeFormData({ ...officeFormData, officeemail: email, officeName: email.split('@')[0] });
  };

  const loadOffices = useCallback(() => {
    if (user?.role === 'Admin' || user?.role === 'superadmin') dispatch(fetchOffices());
    else if (user?.dealerId) dispatch(fetchDealerOffices(user.dealerId));
    else dispatch(fetchOffices());
  }, [dispatch, user]);

  // Fetch once for the current user.
  useEffect(() => {
    loadOffices();
  }, [loadOffices]);

  useEffect(() => {
    if (!refreshData) return;
    loadOffices();
    dispatch(setRefreshOfficesData(false));
  }, [refreshData, loadOffices, dispatch]);

  // Handle success/error messages
  useEffect(() => {
    if (successMessage) {
      setAlertMessage(successMessage);
      setAlertType('success');
      setShowAlert(true);
      setTimeout(() => {
        setShowAlert(false);
        dispatch(clearMessages());
      }, 3000);
    }
    if (errorMessage) {
      setAlertMessage(errorMessage);
      setAlertType('error');
      setShowAlert(true);
      setTimeout(() => {
        setShowAlert(false);
        dispatch(clearMessages());
      }, 3000);
    }
  }, [successMessage, errorMessage, dispatch]);

  const getInitials = (name) => {
    if (!name) return '??';
    const words = name.trim().split(/\s+/);
    return `${words[0]?.[0] || ''}${words.length > 1 ? words[words.length - 1][0] : ''}`.toUpperCase();
  };

  const avatarColors = [
    '#2b6e9e', '#7c3aed', '#f59e0b', '#ef4444',
    '#10b981', '#f97316', '#8b5cf6', '#00b894'
  ];

  const getAvatarColor = (name) => {
    if (!name) return '#2b6e9e';
    return avatarColors[name.charCodeAt(0) % avatarColors.length];
  };

  const getDealerName = (office) => {
    return office.dealerName || office.dealer || office.dealerId || '-';
  };

  // Filter offices based on search term
  const filteredOffices = useMemo(() => {
    return allOffices.filter(office =>
      [office.officeName, office.officeemail, office.officeMobile1, getDealerName(office)]
        .some(field => field && field.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }, [allOffices, searchTerm]);

  // Reset to page 1 when search or rows per page changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, rowsPerPage]);

  // Calculate pagination values
  const totalItems = filteredOffices.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / rowsPerPage));

  // Ensure current page is valid
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  // Get current page items
  const getCurrentPageItems = useCallback(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = Math.min(startIndex + rowsPerPage, totalItems);
    return filteredOffices.slice(startIndex, endIndex);
  }, [currentPage, rowsPerPage, filteredOffices, totalItems]);

  const currentItems = getCurrentPageItems();

  // Pagination handlers
  const handlePageChange = useCallback((page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      setGoToPage('');
      // Scroll to top of table
      const tableElement = document.querySelector('.table-scroll');
      if (tableElement) {
        tableElement.scrollTop = 0;
      }
    }
  }, [totalPages]);

  const handleRowsPerPageChange = useCallback((e) => {
    const newRowsPerPage = Number(e.target.value);
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
    setGoToPage('');
  }, []);

  const handleGoToPage = useCallback((e) => {
    e.preventDefault();
    const pageNum = parseInt(goToPage);
    if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= totalPages) {
      setCurrentPage(pageNum);
      setGoToPage('');
    }
  }, [goToPage, totalPages]);

  // Generate page numbers for display - FIXED
  const getPageNumbers = useCallback(() => {
    const pages = [];
    const maxVisible = 5;
    const halfVisible = Math.floor(maxVisible / 2);

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      let startPage = Math.max(1, currentPage - halfVisible);
      let endPage = Math.min(totalPages, currentPage + halfVisible);

      // Adjust if we're at the beginning
      if (currentPage - halfVisible < 1) {
        endPage = Math.min(totalPages, endPage + (halfVisible - currentPage + 1));
      }
      
      // Adjust if we're at the end
      if (currentPage + halfVisible > totalPages) {
        startPage = Math.max(1, startPage - (currentPage + halfVisible - totalPages));
      }

      // Add first page and ellipsis if needed
      if (startPage > 1) {
        pages.push(1);
        if (startPage > 2) {
          pages.push('...');
        }
      }

      // Add middle pages
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      // Add last page and ellipsis if needed
      if (endPage < totalPages) {
        if (endPage < totalPages - 1) {
          pages.push('...');
        }
        pages.push(totalPages);
      }
    }

    return pages;
  }, [currentPage, totalPages]);

  // Calculate displayed range
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endItem = Math.min(currentPage * rowsPerPage, totalItems);

  const toggleExpand = (index) => {
    setExpandedRow(expandedRow === index ? null : index);
  };

  // Handle Add Office
  const handleAddOffice = () => {
    setOfficeFormData({
      officeName: '',
      officeMobile1: '',
      officeMobile2: '',
      phoneCode1: '+1', phoneCode2: '+1',
      officeemail: '',
      officepassword: '',
      officeaddress: '',
      city: '',
      state: '',
      zipcode: '',
      isbar: '0',
      officelicense: '',
      dealerId: user?.dealerId || ''
    });
    setShowPassword(false);
    setIsAddModalOpen(true);
  };

  // Handle Edit Office
  const handleEditOffice = (office) => {
    setEditingOffice(office);
    setOfficeFormData({
      officeName: office.officeName || '',
      officeMobile1: splitPhone(office.officeMobile1).number,
      phoneCode1: splitPhone(office.officeMobile1).code,
      officeMobile2: splitPhone(office.officeMobile2).number,
      phoneCode2: splitPhone(office.officeMobile2).code,
      officeemail: office.officeemail || '',
      officepassword: office.officepassword || office.officePassword || office.password || '',
      officeaddress: office.officeaddress || '',
      city: office.city || '',
      state: office.state || '',
      zipcode: office.zipcode || '',
      isbar: office.isbar || '0',
      officelicense: office.officelicense || '',
      dealerId: office.dealerId || user?.dealerId || ''
    });
    setShowPassword(false);
    setIsEditModalOpen(true);
  };

  // Submit Add Office
  const handleAddSubmit = async (e) => {
    e.preventDefault();

    const officeData = {
      officeName: officeFormData.officeName,
      officeMobile1: officeFormData.officeMobile1 ? officeFormData.phoneCode1 + officeFormData.officeMobile1 : '',
      officeMobile2: officeFormData.officeMobile2 ? officeFormData.phoneCode2 + officeFormData.officeMobile2 : '',
      officeemail: officeFormData.officeemail,
      officepassword: officeFormData.officepassword,
      officeaddress: officeFormData.officeaddress,
      city: officeFormData.city,
      state: officeFormData.state,
      zipcode: officeFormData.zipcode,
      isbar: officeFormData.isbar,
      officelicense: officeFormData.officelicense || '',
      dealerId: user?.dealerId || officeFormData.dealerId
    };

    await dispatch(addOfficeAsync(officeData));
    setIsAddModalOpen(false);
    resetForm();
  };

  // Submit Edit Office
  const handleEditSubmit = async (e) => {
    e.preventDefault();

    const officeData = {
      officeName: officeFormData.officeName,
      officeMobile1: officeFormData.officeMobile1 ? officeFormData.phoneCode1 + officeFormData.officeMobile1 : '',
      officeMobile2: officeFormData.officeMobile2 ? officeFormData.phoneCode2 + officeFormData.officeMobile2 : '',
      officeemail: officeFormData.officeemail,
      officeaddress: officeFormData.officeaddress,
      city: officeFormData.city,
      state: officeFormData.state,
      zipcode: officeFormData.zipcode,
      isbar: officeFormData.isbar,
      officelicense: officeFormData.officelicense || '',
      dealerId: user?.dealerId || officeFormData.dealerId
    };

    // Only include password if it's provided
    if (officeFormData.officepassword) {
      officeData.officepassword = officeFormData.officepassword;
    }

    await dispatch(editOfficeAsync({
      officeId: editingOffice.officeId,
      officeData: officeData
    })).unwrap();
    await loadOffices();
    setIsEditModalOpen(false);
    resetForm();
  };

  const resetForm = () => {
    setOfficeFormData({
      officeName: '',
      officeMobile1: '',
      officeMobile2: '',
      officeemail: '',
      officepassword: '',
      officeaddress: '',
      city: '',
      state: '',
      zipcode: '',
      isbar: '0',
      officelicense: '',
      dealerId: ''
    });
    setEditingOffice(null);
  };

  const handleDeleteClick = (office) => {
    setSelectedOffice(office);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    const officeToDelete = selectedOffice;
    setIsDeleteModalOpen(false);
    setSelectedOffice(null);

    if (officeToDelete) {
      await dispatch(deleteOfficeAsync(officeToDelete.officeId));
    }
  };

  // Expanded Row Component
  const ExpandedRow = ({ office, isOpen }) => {
    if (!isOpen) return null;

    const detailItems = [
      { icon: '📋', label: 'ID', value: office.officeId },
      { icon: '🏢', label: 'Name', value: office.officeName },
      { icon: '📱', label: 'Phone', value: office.officeMobile1 },
      { icon: '📞', label: 'Phone 2', value: office.officeMobile2 },
      { icon: '✉️', label: 'Email', value: office.officeemail },
      { icon: '📍', label: 'Address', value: office.officeaddress },
      { icon: '🏙️', label: 'City', value: office.city },
      { icon: '🗺️', label: 'State', value: office.state },
      { icon: '📮', label: 'Zipcode', value: office.zipcode },
      { icon: '📜', label: 'BAR License', value: office.isbar === '1' ? 'Yes' : 'No' },
      { icon: '🔑', label: 'License', value: office.officelicense },
      { icon: '👤', label: 'Dealer', value: getDealerName(office) },
    ];

    return (
      <tr className="expanded-row">
        <td colSpan="8">
          <div className="expanded-content">
            <div className="expanded-header">
              <div className="expanded-header-icon">🏢</div>
              <div>
                <h3 className="expanded-header-title">Officer Details</h3>
                <div className="expanded-header-subtitle">
                  Complete information for {office.officeName}
                </div>
              </div>
            </div>
            <div className="expanded-list-view">
              <div className="expanded-two-columns">
                {detailItems.map((item, idx) => (
                  <div key={idx} className="expanded-list-item">
                    <div className="list-item-icon">{item.icon}</div>
                    <div className="list-item-label">{item.label}:</div>
                    <div className="list-item-value">
                      {item.value && item.value !== 'undefined' ? item.value : '-'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </td>
      </tr>
    );
  };

  // Office Card Component for Mobile View
  const OfficeCard = ({ office, index }) => {
    const [showDetails, setShowDetails] = useState(false);

    const detailItems = [
      { icon: '📋', label: 'Office ID', value: office.officeId },
      { icon: '📞', label: 'Phone 2', value: office.officeMobile2 },
      { icon: '📍', label: 'Address', value: office.officeaddress },
      { icon: '🏙️', label: 'City', value: office.city },
      { icon: '🗺️', label: 'State', value: office.state },
      { icon: '📮', label: 'Zipcode', value: office.zipcode },
      { icon: '📜', label: 'BAR License', value: office.isbar === '1' ? 'Yes' : 'No' },
      { icon: '🔑', label: 'License', value: office.officelicense },
    ];

    return (
      <div className="office-card-wrapper">
        <div className="office-card">
          <div className="card-header">
            <div className="card-avatar-wrap">
              <div className="avatar" style={{ background: getAvatarColor(office.officeName) }}>
                {getInitials(office.officeName)}
              </div>
              <div className="card-identity">
                <span className="card-name">{office.officeName}</span>
                <span className="card-dealer">{getDealerName(office)}</span>
              </div>
            </div>
            <div className="card-actions-header">
              <button className="card-action-icon edit" title="Edit" onClick={() => handleEditOffice(office)}>
                <svg viewBox="0 0 18 18" fill="currentColor" width="14" height="14">
                  <path d="M12.5 2.5L15.5 5.5L6 15H3V12L12.5 2.5Z" stroke="currentColor" fill="none" strokeWidth="1.2" />
                  <path d="M10.5 4.5L13.5 7.5" stroke="currentColor" strokeWidth="1.2" />
                </svg>
              </button>
              <button className="card-action-icon delete" title="Delete" onClick={() => handleDeleteClick(office)}>
                <TrashIcon />
              </button>
              <button
                className={`card-more-btn ${showDetails ? 'active' : ''}`}
                onClick={() => setShowDetails(!showDetails)}
              >
                {showDetails ? 'Less' : 'More'}
              </button>
            </div>
          </div>
          <div className="card-body">
            <div className="card-row">
              <span className="card-label">Phone</span>
              <span className="card-value phone">{office.officeMobile1 || '-'}</span>
            </div>
            <div className="card-row">
              <span className="card-label">Email</span>
              <span className="card-value">{office.officeemail || '-'}</span>
            </div>
          </div>

          {showDetails && (
            <div className="card-expanded-details">
              <div className="card-expanded-section">
                <h4>📋 Office Information</h4>
                <div className="card-details-grid">
                  {detailItems.map((item, idx) => (
                    <div key={idx} className="card-detail-item">
                      <div className="card-detail-label">{item.icon} {item.label}</div>
                      <div className="card-detail-value">
                        {item.value && item.value !== 'undefined' ? item.value : '-'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading && allOffices.length === 0) {
    return (
      <div className="offices-root">
        <div className="offices-wrapper">
          <SkeletonTable title="Officers" headings={["SNO", "Name", "Email", "Phone", "Dealer", "Actions", "Delete", "More"]} />
        </div>
      </div>
    );
  }

  return (
    <div className="offices-root">
      <div className="offices-wrapper">
        {/* Alert Banner */}
        {showAlert && (
          <div className={`alert-banner ${alertType}`}>
            <span>{alertMessage}</span>
            <button className="alert-close" onClick={() => setShowAlert(false)}>✕</button>
          </div>
        )}

        <div className="page-header">
          <div className="header-left">
            <h1 className="page-title">OFFICERS</h1>
          </div>
          <div className="header-right">
            <div className="stat-pill">
              <span className="stat-dot" />
              <span className="stat-label">Records</span>
              <span className="stat-value">{filteredOffices.length}</span>
            </div>
            {(user?.role === 'Admin' || user?.role === 'superadmin' || user?.role === 'dealer') && (
              <button className="add-btn" onClick={handleAddOffice}>+ Add Officer</button>
            )}
          </div>
        </div>

        <div className="toolbar">
          <div className="search-wrap">
            <svg className="search-icon" viewBox="0 0 20 20" fill="none">
              <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.8" />
              <path d="M13.5 13.5L17 17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
            <input
              className="search-input"
              type="text"
              placeholder="Search officers..."
              value={searchTerm}
              onChange={(e) => { 
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
            {searchTerm && (
              <button className="search-clear" onClick={() => {
                setSearchTerm('');
                setCurrentPage(1);
              }}>✕</button>
            )}
          </div>
          <div className="rows-control">
            <span>Show</span>
            <select 
              value={rowsPerPage} 
              onChange={handleRowsPerPageChange}
            >
              {[5, 10, 25, 50, 100].map(n => <option key={n}>{n}</option>)}
            </select>
          </div>
        </div>

        {filteredOffices.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">⊘</div>
            <p>No offices found</p>
            {searchTerm && (
              <button className="reset-btn" onClick={() => {
                setSearchTerm('');
                setCurrentPage(1);
              }}>Clear search</button>
            )}
          </div>
        ) : (
          <>
            {/* Desktop/Laptop Table View */}
            <div className="table-view">
              <div className="table-card">
                <div className="table-scroll">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>SNO</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Dealer</th>
                        <th className="text-center">Actions</th>
                        <th className="text-center">Delete</th>
                        <th className="text-center">More</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentItems.map((office, idx) => {
                        const rowIndex = (currentPage - 1) * rowsPerPage + idx;
                        const isExpanded = expandedRow === rowIndex;
                        return (
                          <React.Fragment key={office.officeId || idx}>
                            <tr className="data-row">
                              <td className="row-index">{(currentPage - 1) * rowsPerPage + idx + 1}</td>
                              <td className="name-cell">
                                <div className="name-with-avatar">
                                  <div className="mini-avatar" style={{ background: getAvatarColor(office.officeName) }}>
                                    {getInitials(office.officeName)}
                                  </div>
                                  <span className="office-name">{office.officeName}</span>
                                </div>
                              </td>
                              <td className="email-cell">
                                <span className="office-email">{office.officeemail || '-'}</span>
                              </td>
                              <td className="phone-cell">
                                <span className="phone-number">{office.officeMobile1 || '-'}</span>
                              </td>
                              <td className="dealer-cell">
                                <span className="dealer-name">{getDealerName(office)}</span>
                              </td>
                              <td>
                                <button className="action-icon edit-icon" title="Edit" onClick={() => handleEditOffice(office)}>
                                  <svg viewBox="0 0 18 18" fill="currentColor" width="14" height="14">
                                    <path d="M12.5 2.5L15.5 5.5L6 15H3V12L12.5 2.5Z" stroke="currentColor" fill="none" strokeWidth="1.2" />
                                    <path d="M10.5 4.5L13.5 7.5" stroke="currentColor" strokeWidth="1.2" />
                                  </svg>
                                </button>
                              </td>
                              <td>
                                <button className="action-icon delete-icon" title="Delete" onClick={() => handleDeleteClick(office)}>
                                  <TrashIcon />
                                </button>
                              </td>
                              <td className="more-cell">
                                <button
                                  className={`more-btn ${isExpanded ? 'active' : ''}`}
                                  onClick={() => toggleExpand(rowIndex)}
                                >
                                  {isExpanded ? 'Less' : 'More'}
                                </button>
                              </td>
                            </tr>
                            <ExpandedRow office={office} isOpen={isExpanded} />
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="pagination-bar">
                  <div className="pagination-info">
                    Showing <strong>{startItem}–{endItem}</strong> of <strong>{totalItems}</strong>
                  </div>
                  <div className="pagination-controls">
                    <button 
                      className="pg-btn" 
                      onClick={() => handlePageChange(1)} 
                      disabled={currentPage === 1}
                    >
                      «
                    </button>
                    <button 
                      className="pg-btn" 
                      onClick={() => handlePageChange(currentPage - 1)} 
                      disabled={currentPage === 1}
                    >
                      ‹
                    </button>
                    {getPageNumbers().map((p, i) => {
                      if (p === '...') {
                        return <span key={`ellipsis-${i}`} className="pg-dots">…</span>;
                      }
                      return (
                        <button 
                          key={`page-${p}`} 
                          className={`pg-btn ${currentPage === p ? 'active' : ''}`} 
                          onClick={() => handlePageChange(p)}
                        >
                          {p}
                        </button>
                      );
                    })}
                    <button 
                      className="pg-btn" 
                      onClick={() => handlePageChange(currentPage + 1)} 
                      disabled={currentPage === totalPages}
                    >
                      ›
                    </button>
                    <button 
                      className="pg-btn" 
                      onClick={() => handlePageChange(totalPages)} 
                      disabled={currentPage === totalPages}
                    >
                      »
                    </button>
                  </div>
                  <div className="goto-wrap">
                    <span>Go to</span>
                    <form onSubmit={handleGoToPage}>
                      <input
                        type="number"
                        min="1"
                        max={totalPages}
                        value={goToPage}
                        onChange={e => setGoToPage(e.target.value)}
                        placeholder="—"
                      />
                      <button type="submit">↵</button>
                    </form>
                  </div>
                </div>
              </div>
            </div>

            {/* Tablet/Mobile Card View */}
            <div className="card-view">
              <div className="cards-grid">
                {currentItems.map((office, idx) => (
                  <OfficeCard key={office.officeId || idx} office={office} index={idx} />
                ))}
              </div>
              <div className="pagination-bar mobile-pagination">
                <div className="pagination-info">
                  {startItem}-{endItem} of {totalItems}
                </div>
                <div className="pagination-controls">
                  <button 
                    className="pg-btn" 
                    onClick={() => handlePageChange(currentPage - 1)} 
                    disabled={currentPage === 1}
                  >
                    ‹
                  </button>
                  <span className="page-current">{currentPage}/{totalPages}</span>
                  <button 
                    className="pg-btn" 
                    onClick={() => handlePageChange(currentPage + 1)} 
                    disabled={currentPage === totalPages}
                  >
                    ›
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Add Office Modal */}
      {isAddModalOpen && (
        <div className="modal-overlay" onClick={() => setIsAddModalOpen(false)}>
          <div className="modal-container add-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Officer</h2>
              <button className="modal-close" onClick={() => setIsAddModalOpen(false)}>✕</button>
            </div>
            <form onSubmit={handleAddSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Email <span className="required">*</span></label>
                  <input
                    type="email"
                    autoComplete="off"
                    value={officeFormData.officeemail}
                    onChange={(e) => onEmailInput(e.target.value)}
                    required
                  />
                  {/* <small className="field-note">Email will be used to generate office name</small> */}
                </div>

                <div className="form-group">
                  <label>Officer Name</label>
                  <input
                    type="text"
                    value={officeFormData.officeName}
                    onChange={(e) => setOfficeFormData({ ...officeFormData, officeName: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Password <span className="required">*</span></label>
                  <div className="password-input-wrapper">
                    <input type={showPassword ? 'text' : 'password'} autoComplete="new-password" value={officeFormData.officepassword} onChange={(e) => setOfficeFormData({ ...officeFormData, officepassword: e.target.value })} required />
                    <button type="button" className="password-toggle" aria-label={showPassword ? 'Hide password' : 'Show password'} onClick={() => setShowPassword(value => !value)}>{showPassword ? <EyeOff size={20} /> : <Eye size={20} />}</button>
                  </div>
                </div>

                <div className="form-group">
                  <label>Phone</label>
                  <div className="phone-input-group">
                    <select className="country-code" value={officeFormData.phoneCode1} onChange={(e) => setOfficeFormData({ ...officeFormData, phoneCode1: e.target.value })}><option value="+1">+1 (US)</option><option value="+91">+91 (IN)</option><option value="+44">+44 (UK)</option></select>
                    <input type="tel" inputMode="numeric" maxLength={10} value={officeFormData.officeMobile1} onChange={(e) => setOfficeFormData({ ...officeFormData, officeMobile1: digitsOnly(e.target.value, 10) })} />
                  </div>
                </div>

                <div className="form-group">
                  <label>Phone 2</label>
                  <div className="phone-input-group">
                    <select className="country-code" value={officeFormData.phoneCode2} onChange={(e) => setOfficeFormData({ ...officeFormData, phoneCode2: e.target.value })}><option value="+1">+1 (US)</option><option value="+91">+91 (IN)</option><option value="+44">+44 (UK)</option></select>
                    <input type="tel" inputMode="numeric" maxLength={10} value={officeFormData.officeMobile2} onChange={(e) => setOfficeFormData({ ...officeFormData, officeMobile2: digitsOnly(e.target.value, 10) })} />
                  </div>
                </div>

                <div className="form-group">
                  <label>Address</label>
                  <input
                    type="text"
                    value={officeFormData.officeaddress}
                    onChange={(e) => setOfficeFormData({ ...officeFormData, officeaddress: e.target.value })}
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>City</label>
                    <input
                      type="text"
                      value={officeFormData.city}
                      onChange={(e) => setOfficeFormData({ ...officeFormData, city: cityOnly(e.target.value) })}
                    />
                  </div>
                  <div className="form-group">
                    <label>State</label>
                    <select
                      value={officeFormData.state}
                      onChange={(e) => setOfficeFormData({ ...officeFormData, state: e.target.value })}
                    >
                      <option value="">Select State</option>
                      {usStates.map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Zipcode</label>
                    <input
                      type="text"
                      value={officeFormData.zipcode}
                      onChange={(e) => setOfficeFormData({ ...officeFormData, zipcode: digitsOnly(e.target.value, 6) })}
                    />
                  </div>
                </div>

                <div className="checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={officeFormData.isbar === '1'}
                      onChange={(e) => setOfficeFormData({ ...officeFormData, isbar: e.target.checked ? '1' : '0' })}
                    />
                    <span>isBAR License</span>
                  </label>
                </div>

                {/* <div className="form-group">
                  <label>License Number</label>
                  <input
                    type="text"
                    value={officeFormData.officelicense}
                    onChange={(e) => setOfficeFormData({ ...officeFormData, officelicense: e.target.value })}
                    placeholder="ARD302083"
                  />
                </div> */}
              </div>
              <div className="modal-footer">
                <button type="button" className="cancel-btn" onClick={() => setIsAddModalOpen(false)}>Cancel</button>
                <button type="submit" className="submit-btn">SAVE</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Office Modal */}
      {isEditModalOpen && editingOffice && (
        <div className="modal-overlay" onClick={() => setIsEditModalOpen(false)}>
          <div className="modal-container edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Office</h2>
              <button className="modal-close" onClick={() => setIsEditModalOpen(false)}>✕</button>
            </div>
            <form onSubmit={handleEditSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Officer Name <span className="required">*</span></label>
                  <input
                    type="text"
                    value={officeFormData.officeName}
                    onChange={(e) => setOfficeFormData({ ...officeFormData, officeName: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Email <span className="required">*</span></label>
                  <input
                    type="email"
                    value={officeFormData.officeemail}
                    onChange={(e) => setOfficeFormData({ ...officeFormData, officeemail: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Password</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      value={officeFormData.officepassword}
                      onChange={(e) => setOfficeFormData({ ...officeFormData, officepassword: e.target.value })}
                    />
                    <button type="button" className="password-toggle" aria-label={showPassword ? 'Hide password' : 'Show password'} onClick={() => setShowPassword(value => !value)}>
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label>Phone <span className="required">*</span></label>
                  <div className="phone-input-group">
<select className="country-code" value={officeFormData.phoneCode1} onChange={(e) => setOfficeFormData({ ...officeFormData, phoneCode1: e.target.value })}><option value="+1">+1</option><option value="+91">+91</option><option value="+44">+44</option></select>
<input
                    type="tel"
                    inputMode="numeric"
                    maxLength={10}
                    value={officeFormData.officeMobile1}
                    onKeyDown={preventNonNumericInput}
                    onChange={(e) => setOfficeFormData({ ...officeFormData, officeMobile1: digitsOnly(e.target.value, 10) })}
                    required
                  />
</div>
                </div>

                <div className="form-group">
                  <label>Phone 2</label>
                  <div className="phone-input-group">
<select className="country-code" value={officeFormData.phoneCode2} onChange={(e) => setOfficeFormData({ ...officeFormData, phoneCode2: e.target.value })}><option value="+1">+1</option><option value="+91">+91</option><option value="+44">+44</option></select>
<input
                    type="tel"
                    inputMode="numeric"
                    maxLength={10}
                    value={officeFormData.officeMobile2}
                    onKeyDown={preventNonNumericInput}
                    onChange={(e) => setOfficeFormData({ ...officeFormData, officeMobile2: digitsOnly(e.target.value, 10) })}
                  />
</div>
                </div>

                <div className="form-group">
                  <label>Address <span className="required">*</span></label>
                  <input
                    type="text"
                    value={officeFormData.officeaddress}
                    onChange={(e) => setOfficeFormData({ ...officeFormData, officeaddress: e.target.value })}
                    required
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>City</label>
                    <input
                      type="text"
                      value={officeFormData.city}
                      onChange={(e) => setOfficeFormData({ ...officeFormData, city: cityOnly(e.target.value) })}
                    />
                  </div>
                  <div className="form-group">
                    <label>State <span className="required">*</span></label>
                    <select
                      value={officeFormData.state}
                      onChange={(e) => setOfficeFormData({ ...officeFormData, state: e.target.value })}
                      required
                    >
                      <option value="">Select State</option>
                      {usStates.map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Zipcode <span className="required">*</span></label>
                    <input
                      type="text"
                      value={officeFormData.zipcode}
                      inputMode="numeric"
                      maxLength={6}
                      onKeyDown={preventNonNumericInput}
                      onChange={(e) => setOfficeFormData({ ...officeFormData, zipcode: digitsOnly(e.target.value, 6) })}
                      required
                    />
                  </div>
                </div>

                <div className="checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={officeFormData.isbar === '1'}
                      onChange={(e) => setOfficeFormData({ ...officeFormData, isbar: e.target.checked ? '1' : '0' })}
                    />
                    <span>isBAR License</span>
                  </label>
                </div>

                <div className="form-group">
                  <label>License Number</label>
                  <input
                    type="text"
                    value={officeFormData.officelicense}
                    onChange={(e) => setOfficeFormData({ ...officeFormData, officelicense: e.target.value })}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="cancel-btn" onClick={() => setIsEditModalOpen(false)}>Cancel</button>
                <button type="submit" className="submit-btn">UPDATE</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && selectedOffice && (
        <div className="modal-overlay" onClick={() => setIsDeleteModalOpen(false)}>
          <div className="modal-container delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="delete-modal-content">
              <div className="delete-icon">🗑️</div>
              <h3>Are you sure? You want to delete Office - {selectedOffice.officeName}</h3>
              <p>You can't revert your action</p>
              <div className="delete-modal-actions">
                <button className="delete-confirm-btn" onClick={handleConfirmDelete}>Yes Delete it!</button>
                <button className="delete-cancel-btn" onClick={() => setIsDeleteModalOpen(false)}>No, Keep it!</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Offices;
